import React, { useEffect, useState, useRef } from "react";
import {
  IoBarbellOutline,
  IoTimeOutline,
  IoRestaurantOutline,
  IoFlameOutline,
  IoNutritionOutline,
  IoLeafOutline,
  IoWaterOutline,
  IoInformationCircleOutline,
  IoCloseOutline,
  IoCheckmarkCircleOutline
} from "react-icons/io5";
import KitchenIcon from "../components/kitchenicons";
import SadEggplant from "../resources/images/sad-eggplant.png";
import "../styles/recipe.css";

export default function RecipePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recipe, setRecipe] = useState(null);
  const [activeTab, setActiveTab] = useState("ingredients"); // mobile

  // Modals
  const [needAgb, setNeedAgb] = useState(false);
  const [agbChecked, setAgbChecked] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);

  const promoTimerRef = useRef(null);

  const getTokenFromUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("token") || "";
    } catch {
      return "";
    }
  };
  const token = getTokenFromUrl();


  useEffect(() => {
    const block = needAgb; 
    document.body.style.overflow = block ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [needAgb]);

  // AGB: prüfen
  useEffect(() => {
    const accepted = localStorage.getItem("mila_agbAccepted_v1") === "true";
    if (!accepted) setNeedAgb(true);
  }, []);


  useEffect(() => {
    const agbAccepted = localStorage.getItem("mila_agbAccepted_v1") === "true";
    const promoDismissed = sessionStorage.getItem("mila_promoDismissed_v1") === "true";

    if (agbAccepted && !promoDismissed && !promoTimerRef.current) {
      promoTimerRef.current = setTimeout(() => {
        setPromoOpen(true);
      }, 30000); // 30s
    }

    return () => {
      if (promoTimerRef.current) {
        clearTimeout(promoTimerRef.current);
        promoTimerRef.current = null;
      }
    };
  }, [needAgb]); // wenn AGB zu -> Effekt erneut prüfen

  // API laden
  useEffect(() => {
    const fetchRecipe = async () => {
      if (!token) {
        setError("oops, das wurde nicht gefunden");
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(
          `/api/recipe-share-get?token=${encodeURIComponent(token)}`,
          {
            method: "GET",
          }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRecipe(mapRecipeResponse(data));
      } catch (e) {
        console.error(e);
        setError("oops, das wurde nicht gefunden");
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [token]);

  const mapRecipeResponse = (data) => ({
    title: data.title || data.name || "Rezept",
    category: data.category || data.type || "",
    difficulty: data.difficulty || data.level || "—",
    preparation_time: data.preparation_time || data.time || data.cookTime || "—",
    servings: data.servings ?? data.portions ?? "—",
    nutrition: {
      calories: data.nutrition?.calories ?? data.calories ?? null,
      protein: data.nutrition?.protein ?? data.protein ?? null,
      carbs: data.nutrition?.carbs ?? data.carbs ?? null,
      fat: data.nutrition?.fat ?? data.fat ?? null,
    },
    kitchen_gadgets: data.kitchen_gadgets || data.devices || [],
    ingredients: data.ingredients || [],
    instructions: data.instructions || data.steps || [],
  });

  const roundPerServing = (value) => {
    const servings = Number(recipe?.servings || 0);
    const v = Number(value || 0);
    if (!servings || !v) return 0;
    return Math.round(v / servings);
  };

  // AGB Aktionen
  const handleAcceptAgb = () => {
    if (!agbChecked) return;
    localStorage.setItem("mila_agbAccepted_v1", "true");
    setNeedAgb(false);
  };
  const handleDeclineAgb = () => {
    alert("Um fortzufahren, stimme bitte den AGB zu.");
  };

  // Promo Aktionen
  const closePromo = () => {
    setPromoOpen(false);
    sessionStorage.setItem("mila_promoDismissed_v1", "true");
  };

  if (loading) {
    return (
      <main className="rp-screen">
        <div className="rp-container">
          <div className="rp-skeleton">
            <div className="rp-skel-line w70" />
            <div className="rp-skel-line w40" />
            <div className="rp-skel-line" />
            <div className="rp-skel-line w90" />
            <div className="rp-skel-line w60" />
          </div>
        </div>
        {needAgb && <AgbModal
          checked={agbChecked}
          setChecked={setAgbChecked}
          onAccept={handleAcceptAgb}
          onDecline={handleDeclineAgb}
        />}
      </main>
    );
  }

  if (error || !recipe) {
    return (
      <main className="rp-screen rp-centered">
        <img src={SadEggplant} alt="Traurige Aubergine" style={{ width: 280 }} /> 
        <h1 className="rp-oops">Der Link ist wohl schon abgelaufen...</h1>
        {needAgb && <AgbModal
          checked={agbChecked}
          setChecked={setAgbChecked}
          onAccept={handleAcceptAgb}
          onDecline={handleDeclineAgb}
        />}
      </main>
    );
  }

  const NUTRITION = [
    { Icon: IoFlameOutline, label: `${roundPerServing(recipe?.nutrition?.calories)} kcal`, color: "#f43f5e", bg: "rgba(244,63,94,0.10)" },
    { Icon: IoNutritionOutline, label: `${roundPerServing(recipe?.nutrition?.protein)} g Eiweiß`, color: "#22c55e", bg: "rgba(34,197,94,0.10)" },
    { Icon: IoLeafOutline, label: `${roundPerServing(recipe?.nutrition?.carbs)} g Kohlenhydrate`, color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
    { Icon: IoWaterOutline, label: `${roundPerServing(recipe?.nutrition?.fat)} g Fett`, color: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
  ];

  return (
    <main className="rp-screen">
      <div className="rp-container">
        {/* Titel & Kategorie */}
        <h1 className="rp-title">{recipe.title}</h1>
        {recipe.category && <p className="rp-category">{recipe.category}</p>}

        {/* Info kompakt */}
        <div className="rp-infoRowCompact">
          <div className="rp-infoGroup">
            <div className="rp-infoItem">
              <span className="rp-infoIcon">
                <IoBarbellOutline size={18} color="#7A9476" />
              </span>
              <span className="rp-infoLabel">{recipe.difficulty}</span>
            </div>
            <div className="rp-infoItem">
              <span className="rp-infoIcon">
                <IoTimeOutline size={18} color="#7A9476" />
              </span>
              <span className="rp-infoLabel">{recipe.preparation_time} Min</span>
            </div>
            <div className="rp-infoItem">
              <span className="rp-infoIcon">
                <IoRestaurantOutline size={18} color="#7A9476" />
              </span>
              <span className="rp-infoLabel">{recipe.servings}x</span>
            </div>
          </div>

          <button
            className="rp-infoBtnInline"
            title="Alle Angaben pro Portion, Abweichungen möglich"
            aria-label="Info zu Nährwerten"
          >
            <IoInformationCircleOutline size={18} color="#6b7280" />
          </button>
        </div>

        {/* Nährwerte */}
        <div className="rp-nutritionRow">
          {NUTRITION.map(({ Icon, label, color, bg }, i) => (
            <div
              className="rp-nutritionItem"
              key={i}
              style={{ backgroundColor: bg }}
            >
              <Icon
                size={16}
                color={color}
                style={{ marginRight: 6, minWidth: 16 }}
              />
              <span className="rp-nutritionValue">{label}</span>
            </div>
          ))}
        </div>

        {/* Küchenhelfer */}
        {Array.isArray(recipe.kitchen_gadgets) &&
          recipe.kitchen_gadgets.length > 0 && (
            <>
              <h2 className="rp-section">Küchenhelfer</h2>
              <div className="rp-gadgetRow">
                {recipe.kitchen_gadgets.map((g) => (
                  <div className="rp-gadgetItem" key={g}>
                    {/* akzeptiert sowohl "herd" als auch "stove" etc. */}
                    <KitchenIcon
                      type={g}
                      size={18}
                      color="#79797a"
                      style={{ marginRight: 6 }}
                    />
                    <span className="rp-gadgetText">{g}</span>
                  </div>
                ))}
              </div>
            </>
          )}

        {/* Tabs (nur Mobile sichtbar) */}
        <div className="rp-tabs" role="tablist" aria-label="Rezeptbereiche">
          <button
            className={`rp-tab ${activeTab === "ingredients" ? "active" : ""}`}
            role="tab"
            aria-selected={activeTab === "ingredients"}
            onClick={() => setActiveTab("ingredients")}
          >
            <span
              className={`rp-tabText ${
                activeTab === "ingredients" ? "active" : ""
              }`}
            >
              Zutaten
            </span>
          </button>
          <button
            className={`rp-tab ${activeTab === "instructions" ? "active" : ""}`}
            role="tab"
            aria-selected={activeTab === "instructions"}
            onClick={() => setActiveTab("instructions")}
          >
            <span
              className={`rp-tabText ${
                activeTab === "instructions" ? "active" : ""
              }`}
            >
              Zubereitung
            </span>
          </button>
        </div>

        {/* Inhalt */}
        <div className="rp-content" data-active={activeTab}>
          <section className="rp-col ingredients">
            <h2 className="rp-section">Zutaten</h2>
            <div className="rp-ingredientList">
              {(recipe.ingredients || []).map((ing, i) => (
                <div
                  className="rp-ingredientRow"
                  key={`${ing.ingredient || ing.name || "ing"}-${i}`}
                >
                  <span className="rp-ingredientName">
                    {ing.ingredient || ing.name || "Zutat"}
                  </span>
                  <span className="rp-ingredientAmount">
                    {ing.amount || ing.quantity || ""}
                  </span>
                  <span className="rp-ingredientNutrition">
                    {ing?.nutrients?.calories
                      ? `${ing.nutrients.calories} kcal`
                      : ""}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="rp-col instructions">
            <h2 className="rp-section">Zubereitung</h2>
            <div className="rp-instructionsBox">
              {(recipe.instructions || []).map((step, idx) => (
                <div className="rp-instructionStep" key={idx}>
                  <div className="rp-stepCircle">
                    <span className="rp-stepCircleText">
                      {step.number ?? idx + 1}
                    </span>
                  </div>
                  <p className="rp-instructionsLine">
                    {step.instruction ||
                      step.text ||
                      step.description ||
                      `Schritt ${idx + 1}`}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Modals */}
      {needAgb && (
        <AgbModal
          checked={agbChecked}
          setChecked={setAgbChecked}
          onAccept={handleAcceptAgb}
          onDecline={handleDeclineAgb}
        />
      )}
      {promoOpen && <PromoModal onClose={closePromo} />}
    </main>
  );
}

/* ---------- Modalkomponenten ---------- */

function AgbModal({ checked, setChecked, onAccept, onDecline }) {
  return (
    <div className="rp-modalOverlay" role="dialog" aria-modal="true" aria-labelledby="agb-title">
      <div className="rp-modalCard">
        <div className="rp-modalHeader">
          <h3 id="agb-title" className="rp-modalTitle">AGB Zustimmung</h3>
        </div>
        <div className="rp-modalBody">
          <p className="rp-modalText">
            Um fortzufahren, bestätige bitte, dass du unseren Allgemeinen Geschäftsbedingungen zustimmst.
          </p>
          <label className="rp-checkboxRow">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>Ich habe die AGB gelesen und stimme zu.</span>
          </label>
          <a className="rp-agbLink" href="/agb" target="_blank" rel="noreferrer">AGB öffnen</a>
        </div>
        <div className="rp-modalActions">
          <button className="rp-btnSecondary" onClick={onDecline}>
            Abbrechen
          </button>
          <button
            className={`rp-btnPrimary ${checked ? "" : "disabled"}`}
            disabled={!checked}
            onClick={onAccept}
          >
            <IoCheckmarkCircleOutline size={18} style={{ marginRight: 6 }} />
            Zustimmen
          </button>
        </div>
      </div>
    </div>
  );
}

function PromoModal({ onClose }) {
  return (
    <div className="rp-modalOverlay subtle" role="dialog" aria-modal="true" aria-labelledby="promo-title">
      <div className="rp-promoCard">
        <button className="rp-modalClose" aria-label="Schließen" onClick={onClose}>
          <IoCloseOutline size={20} />
        </button>
        <h3 id="promo-title" className="rp-promoTitle">Gefällt dir dieses Rezept?</h3>
        <p className="rp-promoText">Probiere <strong>RIYU</strong> jetzt selbst aus – erhalte personalisierte Rezepte, Ernährungstipps und mehr.</p>
        <div className="rp-promoActions">
          <a className="rp-btnPrimary" href="/store">
            Jetzt testen
          </a>
          <button className="rp-btnSecondary" onClick={onClose}>
            Später
          </button>
        </div>
      </div>
    </div>
  );
}
